import {z} from "zod"
import {ZodUtils, arrayBufferLike} from "@cm/utils/zod-utils"
import {defaultMaterialParameters} from "#render-nodes/colors"

export namespace DataNodes {
    //TODO: these should be unified with JobNodes.DataObjectReference*, but this would create a circular dependency.
    // They should probably be moved to utils or something.
    export const DataObjectReferenceSchema = z.object({
        type: z.literal("dataObjectReference"),
        dataObjectId: z.number(),
    })

    export type DataObjectReference = z.infer<typeof DataObjectReferenceSchema>

    export const DataSchema = DataObjectReferenceSchema
    export type Data = z.infer<typeof DataSchema>

    export const EvaluationResourcesSchema = z.record(
        z.union([z.string(), z.number()]),
        z.object({
            contentType: z.string(),
            data: arrayBufferLike,
        }),
    )
    export type EvaluationResources = Record<number | string, {contentType: string; data: ArrayBufferLike | Uint8Array}> //TODO: move to a more general place
}

export namespace MeshNodes {
    export const LoadMeshSchema = z.object({
        type: z.literal("loadMesh"),
        data: DataNodes.DataSchema,
    })
    export type LoadMesh = z.infer<typeof LoadMeshSchema>

    export type Subdivide = {
        type: "subdivide"
        input: Mesh
        levels: number
    }
    export const SubdivideSchema: z.ZodType<Subdivide> = z.lazy(() =>
        z.object({
            type: z.literal("subdivide"),
            input: MeshSchema,
            levels: z.number(),
        }),
    )

    //TODO: maybe move MeshData nodes to geometry graph module?
    export type MeshToGeometry = {op: "meshToGeom"; args: [Mesh]}
    export const MeshToGeometrySchema: z.ZodType<MeshToGeometry> = z.lazy(() =>
        z.object({
            op: z.literal("meshToGeom"),
            args: z.tuple([MeshSchema]),
        }),
    )

    export type GeometryExprArg = number | string | GeometryExpr | GeometryExprArg[]
    export const GeometryExprArgSchema: z.ZodType<GeometryExprArg> = z.lazy(() =>
        z.union([z.number(), z.string(), GeometryExprSchema, GeometryExprArgSchema.array()]),
    )

    export type GeometryOperator = {op: string; args: GeometryExprArg[]}
    export const GeometryOperatorSchema: z.ZodType<GeometryOperator> = z.lazy(() =>
        z.object({
            op: z.string(),
            args: GeometryExprArgSchema.array(),
        }),
    )

    export const GeometryExprSchema = z.union([GeometryOperatorSchema, MeshToGeometrySchema])
    export type GeometryExpr = z.infer<typeof GeometryExprSchema>

    export const GeomGraphSchema = z.object({
        type: z.literal("geomGraph"),
        graph: GeometryExprSchema,
    })
    export type GeomGraph = z.infer<typeof GeomGraphSchema>

    export const EmptyMeshSchema = z.object({
        type: z.literal("empty"),
    })
    export type EmptyMesh = z.infer<typeof EmptyMeshSchema>

    export const PlaneMeshSchema = z.object({
        type: z.literal("plane"),
        width: z.number(),
        height: z.number(),
        normalAxis: z.union([z.literal("x+"), z.literal("y+"), z.literal("z+"), z.literal("x-"), z.literal("y-"), z.literal("z-")]),
    })
    export type PlaneMesh = z.infer<typeof PlaneMeshSchema>

    export const MeshSchema = z.union([LoadMeshSchema, SubdivideSchema, GeomGraphSchema, EmptyMeshSchema, PlaneMeshSchema])
    export type Mesh = z.infer<typeof MeshSchema>
}

export namespace RenderNodes {
    // What the render is for, which decides one-shot vs live and how much device memory the frame is
    // charged for: a feedback pass carries no display buffer, and only a final render defers its tiling
    // decision until the scene is on the device.
    export const renderPurpose = z.enum(["feedback", "final", "interactive"])
    export type RenderPurpose = z.infer<typeof renderPurpose>

    export const RenderNodeBaseSchema = z.object({})
    export type RenderNodeBase = z.infer<typeof RenderNodeBaseSchema>

    export type ShaderNode = RenderNodeBase & {
        type: string
        inputs: {[name: string]: [ShaderNode, string] | undefined}
        parameters: {[name: string]: unknown | undefined}
        resources?: {[name: string]: ShaderResources}
    }

    export const ShaderNodeSchema: z.ZodType<ShaderNode> = z.lazy(() =>
        RenderNodeBaseSchema.and(
            z.object({
                type: z.string(),
                inputs: z.record(z.tuple([ShaderNodeSchema, z.string()])),
                parameters: z.record(z.string(), z.unknown()),
                resources: z.record(z.string(), ShaderResourcesSchema).optional(),
            }),
        ),
    )

    export const CameraSchema = RenderNodeBaseSchema.and(
        z.object({
            transform: z.number().array(),
            focalLength: z.number(),
            focalDistance: z.number(),
            sensorSize: z.number(),
            fStop: z.number().optional(),
            exposure: z.number().optional(),
            shiftX: z.number().optional(),
            shiftY: z.number().optional(),
            nearClip: z.number().optional(),
            farClip: z.number().optional(),
        }),
    )
    export type Camera = z.infer<typeof CameraSchema>

    export const MeshSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("mesh").optional(), //Prior to schema version 1, the mesh type was not present in the schema
            id: z.string(),
            meshData: MeshNodes.MeshSchema,
            transform: z.number().array(),
            shaders: z.record(ShaderNodeSchema),
            cryptomatteObjectName: z.string().optional(),
            cryptomatteAssetName: z.string().optional(),
            shadowCatcher: z.boolean().optional(),
            adaptiveSubdivisionRate: z.number().optional(),
            visibleInCamera: z.boolean().optional(),
            visibleInReflections: z.boolean().optional(),
            visibleInRefractions: z.boolean().optional(),
            visibleToShadowRays: z.boolean().optional(),
            visibleToGlossyRays: z.boolean().optional(),
            visibleToDiffuseRays: z.boolean().optional(),
        }),
    )
    export type Mesh = z.infer<typeof MeshSchema>

    export const InstancesSchema = z.discriminatedUnion("type", [
        z.object({
            type: z.literal("transforms"),
            transforms: z.array(z.number().array()),
        }),
        z.object({
            type: z.literal("curveIntersections"),
            meshData: MeshNodes.MeshSchema,
            curvePoints: z.object({
                points: z.array(z.number()),
                normals: z.array(z.number()),
                tangents: z.array(z.number()),
                scales: z.array(z.number()),
            }),
        }),
    ])
    export type Instances = z.infer<typeof InstancesSchema>

    export const MeshInstancesSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("meshInstances"),
            id: z.string(),
            mesh: MeshSchema,
            transform: z.number().array(),
            instances: InstancesSchema,
        }),
    )
    export type MeshInstances = z.infer<typeof MeshInstancesSchema>

    export const ObjectSchema = z.union([MeshSchema, MeshInstancesSchema])
    export type Object = z.infer<typeof ObjectSchema>

    export const LoadImageSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("loadImage"),
            data: DataNodes.DataSchema,
        }),
    )
    export type LoadImage = z.infer<typeof LoadImageSchema>

    export const DistanceTextureSchema = z.object({
        type: z.literal("distanceTexture"),
        meshData: MeshNodes.MeshSchema,
        transform: z.number().array(),
        range: z.number(),
        mergeRange: z.number(),
        width: z.number(),
        height: z.number(),
        forceOriginalResolution: z.boolean().optional(),
        uvChannel: z.number(),
        targets: z.array(ObjectSchema),
        innerValue: z.number().optional(),
        smoothPasses: z.number(),
    })
    export type DistanceTexture = z.infer<typeof DistanceTextureSchema>

    export const ComputeImageSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("computeImage"),
            data: DistanceTextureSchema,
        }),
    )
    export type ComputeImage = z.infer<typeof ComputeImageSchema>

    export const ImageSchema = LoadImageSchema.or(ComputeImageSchema)
    export type Image = z.infer<typeof ImageSchema>

    export const OslSourceSchema = z.object({
        type: z.literal("oslSource"),
        name: z.string(),
        source: z.string(),
    })
    export type OslSource = z.infer<typeof OslSourceSchema>

    export const ShaderResourcesSchema = ImageSchema.or(OslSourceSchema)
    export type ShaderResources = z.infer<typeof ShaderResourcesSchema>

    export const LightPortalSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("portal"),
            id: z.string(),
            transform: z.number().array(),
            width: z.number(),
            height: z.number(),
            round: z.boolean().optional(),
        }),
    )
    export type LightPortal = z.infer<typeof LightPortalSchema>

    export const AreaLightSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("area"),
            id: z.string(),
            transform: z.number().array(),
            width: z.number(),
            height: z.number(),
            strength: z.number(),
            shader: ShaderNodeSchema.optional(),
            round: z.boolean().optional(),
            directionality: z.number().optional(),
            visibleInCamera: z.boolean().optional(),
            visibleInReflections: z.boolean().optional(),
            visibleInRefractions: z.boolean().optional(),
            visibleToShadowRays: z.boolean().optional(),
            visibleToGlossyRays: z.boolean().optional(),
            visibleToDiffuseRays: z.boolean().optional(),
        }),
    )
    export type AreaLight = z.infer<typeof AreaLightSchema>

    export const PointLightSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("point"),
            id: z.string(),
            transform: z.number().array(),
            radius: z.number(),
            strength: z.number(),
            shader: ShaderNodeSchema.optional(),
            visibleInCamera: z.boolean().optional(),
            visibleInReflections: z.boolean().optional(),
            visibleInRefractions: z.boolean().optional(),
            visibleToGlossyRays: z.boolean().optional(),
            visibleToDiffuseRays: z.boolean().optional(),
        }),
    )
    export type PointLight = z.infer<typeof PointLightSchema>

    export const SunLightSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("sun"),
            id: z.string(),
            transform: z.number().array(),
            angle: z.number(),
            strength: z.number(),
            shader: ShaderNodeSchema.optional(),
            visibleInCamera: z.boolean().optional(),
            visibleInReflections: z.boolean().optional(),
            visibleInRefractions: z.boolean().optional(),
            visibleToGlossyRays: z.boolean().optional(),
            visibleToDiffuseRays: z.boolean().optional(),
        }),
    )
    export type SunLight = z.infer<typeof SunLightSchema>

    export const LightSchema = z.union([LightPortalSchema, AreaLightSchema, PointLightSchema, SunLightSchema])
    export type Light = z.infer<typeof LightSchema>

    export const SceneSchema = RenderNodeBaseSchema.and(
        z.object({
            camera: CameraSchema,
            objects: ObjectSchema.array(),
            lights: LightSchema.array(),
            environment: ShaderNodeSchema.optional(),
        }),
    )
    export type Scene = z.infer<typeof SceneSchema>

    export const PassNameSchema = z.union([
        z.literal("Combined"),
        z.literal("Depth"),
        z.literal("Mist"),
        z.literal("Roughness"),
        z.literal("SampleCount"),
        z.literal("Position"),
        z.literal("Normal"),
        z.literal("ObjectID"),
        z.literal("UV"),
        z.literal("Motion"),
        z.literal("MaterialID"),
        z.literal("Diffuse"),
        z.literal("Glossy"),
        z.literal("Volume"),
        z.literal("DiffuseDirect"),
        z.literal("GlossyDirect"),
        z.literal("TransmissionDirect"),
        z.literal("VolumeDirect"),
        z.literal("DiffuseIndirect"),
        z.literal("GlossyIndirect"),
        z.literal("TransmissionIndirect"),
        z.literal("VolumeIndirect"),
        z.literal("DiffuseColor"),
        z.literal("GlossyColor"),
        z.literal("TransmissionColor"),
        z.literal("Emission"),
        z.literal("Background"),
        z.literal("AmbientOcclusion"),
        z.literal("Shadow"),
        ZodUtils.templateLiteral(z.literal("CryptoObject"), z.number()),
        ZodUtils.templateLiteral(z.literal("CryptoMaterial"), z.number()),
        ZodUtils.templateLiteral(z.literal("CryptoAsset"), z.number()),
        z.literal("NoisyCombined"),
        z.literal("DenoisingNormal"),
        z.literal("DenoisingAlbedo"),
        z.literal("DenoisingDepth"),
        ZodUtils.templateLiteral(z.literal("AOVColor:"), z.string()),
        ZodUtils.templateLiteral(z.literal("AOVValue:"), z.string()),
        z.literal("ShadowCatcher"),
        z.literal("ShadowCatcherSampleCount"),
        z.literal("ShadowCatcherMatte"),
    ])
    export type PassName = z.infer<typeof PassNameSchema>

    export const SessionSchema = RenderNodeBaseSchema.and(
        z.object({
            options: z
                .object({
                    cloud: z.boolean().optional(),
                    threads: z.number().optional(),
                    gpu: z.boolean().optional(),
                    transparent_background: z.boolean().optional(),
                    transparent_glass: z.boolean().optional(),
                    // BACKWARDS COMPAT (final_render): superseded by render_purpose, kept so graphs written
                    // before it still parse. Remove once no such graph can still be claimed.
                    final_render: z.boolean().optional(),
                    render_purpose: renderPurpose.optional(),
                    use_denoising: z.boolean().optional(),
                    render_tiling: z.enum(["off", "on", "auto"]).optional(),
                    render_tile_size: z.number().optional(),
                    render_tiling_auto_vram_budget_mb: z.number().optional(),
                    denoise_use_gpu: z.boolean().optional(),
                    denoise_tiling: z.enum(["off", "on", "auto"]).optional(),
                    denoise_tile_height: z.number().optional(),
                    use_osl: z.boolean().optional(),
                    adaptive_subdivision_offscreen_dicing_scale: z.number().optional(),
                    passes: PassNameSchema.array().optional(),
                    preview_pass: PassNameSchema.optional(),
                    image_filter_type: z.string().optional(),
                    image_filter_width: z.number().optional(),
                    texture_streaming: z.boolean().optional(),
                })
                .and(z.record(z.any())),
        }),
    )
    export type Session = z.infer<typeof SessionSchema>

    export const RenderSchema = RenderNodeBaseSchema.and(
        z.object({
            type: z.literal("render"),
            schemaVersion: z.number().optional(), //Missing values are considered to be version 0
            session: SessionSchema,
            scene: SceneSchema,
            width: z.number(),
            height: z.number(),
            samples: z.number(),
        }),
    )
    export type Render = z.infer<typeof RenderSchema>

    /**
     * Minimal projection of {@link RenderSchema} carrying only the fields needed to
     * choose a render queue group (see the jobs coordinator's rendering-queue-group).
     * Parsing this instead of the full RenderSchema avoids validating the entire
     * render graph (scene, materials, geometry) just to read a handful of routing
     * fields — a meaningful saving on the small coordinator VM. Unknown keys are
     * ignored, so it parses the same object RenderSchema does. `local` is not part
     * of the typed option set; it rides along via the options record catch-all.
     */
    export const RenderOptionsSchema = z.object({
        width: z.number(),
        height: z.number(),
        session: z.object({
            options: z.object({
                local: z.boolean().optional(),
                gpu: z.boolean().optional(),
                cloud: z.boolean().optional(),
            }),
        }),
    })
    export type RenderOptions = z.infer<typeof RenderOptionsSchema>

    export const NodeSchema = z.union([
        PassNameSchema,
        SessionSchema,
        RenderSchema,
        SceneSchema,
        CameraSchema,
        ObjectSchema,
        LightSchema,
        DataNodes.DataSchema,
        ImageSchema,
        MeshNodes.MeshSchema,
        ShaderNodeSchema,
    ])
    export type Node = z.infer<typeof NodeSchema>

    export function dataObjectReference(legacyId: number): DataNodes.DataObjectReference {
        return {
            type: "dataObjectReference",
            dataObjectId: legacyId,
        }
    }

    export function distanceTexture(data: Omit<DistanceTexture, "type">): DistanceTexture {
        return {
            type: "distanceTexture",
            ...data,
        }
    }

    export function computeImage(data: ComputeImage["data"]): ComputeImage {
        return {
            type: "computeImage",
            data,
        }
    }

    export function loadImage(legacyId: number): LoadImage {
        return {
            type: "loadImage",
            data: dataObjectReference(legacyId),
        }
    }

    export function defaultMaterial(materialIndex: number): ShaderNode {
        const {color, roughness, metalness} = defaultMaterialParameters(materialIndex)
        const principledBsdf: ShaderNode = {
            type: "principled_bsdf",
            inputs: {},
            parameters: {
                base_color: color,
                roughness,
                metallic: metalness,
            },
        }
        return {
            type: "material_output",
            inputs: {
                surface: [principledBsdf, "bsdf"],
            },
            parameters: {
                name: "Default Material",
                use_mis: true,
            },
        }
    }
}

export function isCryptomattePass(passName: RenderNodes.PassName | string) {
    return passName.startsWith("Crypto")
}

export function isAOVPass(passName: RenderNodes.PassName | string) {
    return passName.startsWith("AOV")
}

function findNodes(graph: RenderNodes.Node, predicate: (node: RenderNodes.Node) => boolean): RenderNodes.Node[] {
    const nodes: RenderNodes.Node[] = []
    const visited = new Set()

    const traverse = (node: RenderNodes.Node) => {
        if (typeof node !== "object" || node === null) return
        if (Array.isArray(node) || node instanceof Array) {
            for (const val of Object.values(node)) traverse(val)
        } else if (ArrayBuffer.isView(node) || node instanceof ArrayBuffer || node instanceof Blob) {
            return
        } else {
            if (visited.has(node)) return
            visited.add(node)
            if (predicate(node)) nodes.push(node)
            for (const value of Object.values(node)) traverse(value)
        }
    }

    traverse(graph)
    return nodes
}

type LegacyId = number

export function gatherDataObjectReferences(graph: RenderNodes.Node): LegacyId[] {
    const nodes = findNodes(graph, (n: RenderNodes.Node) => typeof n === "object" && "type" in n && n["type"] === "dataObjectReference")
    return Array.from(new Set(nodes.map((n) => (n as {dataObjectId: number})["dataObjectId"])))
}

export type ImageTextureNodeRef = {
    dataObjectId: number
    node: RenderNodes.ShaderNode
}

export function gatherImageTextureNodeRefs(graph: RenderNodes.Node): ImageTextureNodeRef[] {
    const imageTextureNodes = findNodes(graph, (n) => typeof n === "object" && n !== null && "type" in n && (n as {type: unknown}).type === "image_texture")
    return imageTextureNodes
        .map((n) => {
            const node = n as RenderNodes.ShaderNode
            const dataRef = node.resources?.image
            if (!dataRef || dataRef.type !== "loadImage") return undefined
            return {dataObjectId: dataRef.data.dataObjectId, node}
        })
        .filter((x): x is ImageTextureNodeRef => x !== undefined)
}
